import { isEuSupplierCountry } from './eu-countries.js';
import { ITALY_COUNTRY_CODE, toCanonicalCountryCode, } from '../functions/country-code.js';
export const REGULARIZATION_CASES = [
    'INTERNAL_REVERSE_CHARGE',
    'INTRA_EU_MISSING_INVOICE',
];
/**
 * Nature N6.x: inversione contabile interna. Il TD20 del caso 1 dichiara la
 * stessa natura che il fornitore avrebbe dovuto scrivere sulla sua fattura.
 */
const INTERNAL_REVERSE_CHARGE_NATURES = [
    'N6_1',
    'N6_2',
    'N6_3',
    'N6_4',
    'N6_5',
    'N6_6',
    'N6_7',
    'N6_8',
    'N6_9',
];
/**
 * Caso 2: `N2.1` per i servizi ricevuti da un soggetto UE e per i beni già
 * presenti in Italia (art. 7-ter / art. 17 c. 2), `N3.2` per gli acquisti
 * intracomunitari veri e propri (art. 41 DL 331/93).
 */
const INTRA_EU_NATURES = [
    'N2_1',
    'N3_2',
];
const RULES = {
    INTERNAL_REVERSE_CHARGE: {
        supplierCountry: 'ITALY',
        vatNatures: INTERNAL_REVERSE_CHARGE_NATURES,
        integrationTypes: ['TD16'],
        // Una sola scelta possibile: l'integrazione dell'inversione contabile
        // interna è sempre un TD16, qualunque N6.x si dichiari.
        defaultIntegrationTypeByVatNature: Object.fromEntries(INTERNAL_REVERSE_CHARGE_NATURES.map((nature) => [nature, 'TD16'])),
        normativeReference: 'Autofattura per regolarizzazione art. 6 c. 9-bis D.Lgs. 471/97',
    },
    INTRA_EU_MISSING_INVOICE: {
        supplierCountry: 'EU_EXCEPT_ITALY',
        vatNatures: INTRA_EU_NATURES,
        integrationTypes: ['TD17', 'TD18', 'TD19'],
        // N2.1 = servizi ricevuti dall'estero ⇒ TD17 (TD19 resta ammesso per i
        // beni già presenti in Italia); N3.2 = acquisto intracomunitario di beni
        // ⇒ TD18.
        defaultIntegrationTypeByVatNature: { N2_1: 'TD17', N3_2: 'TD18' },
        normativeReference: 'Autofattura per acquisto intracomunitario art. 46 c. 5 DL 331/93',
    },
};
/** Regole della casistica, `null` se il valore non è una casistica nota. */
export function getRegularizationCaseRules(regularizationCase) {
    if (!regularizationCase)
        return null;
    return RULES[regularizationCase] ?? null;
}
/** `true` se la Natura è ammessa sul TD20 di quella casistica. */
export function isRegularizationVatNatureAllowed(regularizationCase, vatNature) {
    const rules = getRegularizationCaseRules(regularizationCase);
    return (!!rules && !!vatNature && rules.vatNatures.includes(vatNature));
}
/** `true` se il codice TD può portare l'imposta per quella casistica. */
export function isRegularizationIntegrationTypeAllowed(regularizationCase, electronicType) {
    const rules = getRegularizationCaseRules(regularizationCase);
    return (!!rules &&
        !!electronicType &&
        rules.integrationTypes.includes(electronicType));
}
/**
 * Codice TD proposto per il documento che porta l'imposta, dato il caso e la
 * Natura dichiarata sul TD20. `null` se la coppia non è ammessa.
 */
export function getDefaultRegularizationIntegrationType(regularizationCase, vatNature) {
    const rules = getRegularizationCaseRules(regularizationCase);
    if (!rules || !vatNature)
        return null;
    return (rules.defaultIntegrationTypeByVatNature[vatNature] ?? null);
}
/**
 * Casistica implicata da una Natura. È il percorso inverso usato da chi
 * riceve un TD20 già scritto — pre-check di invio e mapper — che non ha la
 * scelta dell'utente ma solo le righe del documento. `null` se la Natura non
 * appartiene a nessuna casistica.
 */
export function getRegularizationCaseByVatNature(vatNature) {
    if (!vatNature)
        return null;
    return (REGULARIZATION_CASES.find((regularizationCase) => isRegularizationVatNatureAllowed(regularizationCase, vatNature)) ?? null);
}
/**
 * `true` se il paese dell'anagrafica fornitore è ammesso come cedente del
 * TD20 di quella casistica. Il confronto normalizza spazi e maiuscole perché
 * il paese arriva dall'anagrafica, non da un enum.
 */
export function isRegularizationSupplierCountryAllowed(regularizationCase, country) {
    const rules = getRegularizationCaseRules(regularizationCase);
    if (!rules)
        return false;
    const code = toCanonicalCountryCode(country);
    if (rules.supplierCountry === 'ITALY') {
        // Paese assente = Italia, come in tutto il resto del percorso FatturaPA.
        // Le grafie estese ("Italia", "ITA") NON passano: `IdPaese` del tracciato
        // è ISO alpha-2, e il pre-check di invio le rifiuta comunque.
        return code === null || code === ITALY_COUNTRY_CODE;
    }
    return isEuSupplierCountry(code);
}
//# sourceMappingURL=regularization-cases.js.map